import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { timeDelta } from '../utils';
import NotificationService from '../../services/notificationService';
import CounterBidPopup from '../notifications/CounterBidPopup.jsx';
import { useActiveLeague } from '../../hooks/useActiveLeague';
import { resolveNotificationNavigationPath } from '../../utils/notificationNavigation';

const NotificationsDropdown = ({
  isOpen,
  onClose,
  hasError,
  onRetry,
  filter, // 'all' or 'unread'
  onFilterChange,
  totalCount,
  unreadCount,
  onRefresh,
  isRefreshing = false,
  currentTeamId,
  refreshSignal = 0
}) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const dropdownRef = useRef(null);
  const loadRequestSeqRef = useRef(0);
  const navigate = useNavigate();
  const { activeLeague } = useActiveLeague();
  const activeLeagueSlug = activeLeague?.slug;
  const [counterPopup, setCounterPopup] = useState({ isOpen: false, bidId: null, amount: null, leagueId: null });

  const resolveLeagueIdForNotification = (notification) => (
    notification?.data?.leagueId
    || notification?.leagueId
    || notification?.league?._id
    || activeLeague?._id
    || null
  );

  // Load notifications
  const loadNotifications = useCallback(async () => {
    if (!isOpen) return;
    
    console.log('Fetching notifications...');
    const requestSeq = loadRequestSeqRef.current + 1;
    loadRequestSeqRef.current = requestSeq;
    setLoading(true);
    try {
      // Use real data from the backend
      console.log('Making API call to get notifications');
      const params = {
        limit: 10, // Or manage with state for pagination
        offset: 0, // Or manage with state for pagination
        read: filter === 'all' ? undefined : false,
        leagueSlug: activeLeagueSlug,
      };
      const response = await NotificationService.getNotifications(params);
      console.log('Notifications API response:', response);

      if (requestSeq !== loadRequestSeqRef.current) {
        return;
      }
      
      // Only use real notifications from the backend
      if (response && response.notifications) {
        console.log(`Setting ${response.notifications.length} notifications from API`);
        setNotifications(response.notifications);
      } else {
        console.log('No notifications found in API response, setting empty array');
        setNotifications([]);
      }
      
      setError(null);
    } catch (err) {
      if (requestSeq !== loadRequestSeqRef.current) {
        return;
      }

      // Log detailed error information
      console.error('Error fetching notifications:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      // Set appropriate error message
      if (err.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to load notifications');
      }
      
      // Don't use hardcoded data, just set to empty array
      setNotifications([]);
    } finally {
      if (requestSeq === loadRequestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [activeLeagueSlug, filter, isOpen]);

  // Delete/mark notification as read
  const dismissNotification = async (id) => {
    console.log(`Dismissing notification with ID: ${id}`);
    try {
      // Use real data from the backend
      console.log('Making API call to mark notification as read');
      await NotificationService.markAsRead(id);
      console.log('Notification marked as read successfully');
      
      // Remove from local state
      setNotifications(notifications.filter(notif => notif._id !== id));
      
      // Trigger badge count refresh
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      // Log detailed error information
      console.error('Error dismissing notification:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      // In case of error, still remove from local state for better UX
      setNotifications(notifications.filter(notif => notif._id !== id));
      
      // Still trigger badge count refresh even on error for consistency
      if (onRefresh) {
        onRefresh();
      }
    }
  };

  // Map UI action keys (accept_bid, reject_bid, counter_bid) to API actions
  const ACTION_MAP = {
    accept_bid: 'accept',
    reject_bid: 'reject',
    counter_bid: 'counter'
  };

  // Handle bid actions
  const handleBidAction = async (bidId, actionKey, notificationId) => {
    const notification = notifications.find(n => n._id === notificationId);
    const notificationLeagueId = resolveLeagueIdForNotification(notification);
    if (!notification || !notificationLeagueId) {
      console.warn('Cannot respond to bid notification without league information.', { notificationId, bidId });
      toast.error('Could not respond to this offer. Refresh notifications and try again.');
      return;
    }

    // Open popup if counter button clicked
    if (actionKey === 'counter_bid') {
      setCounterPopup({
        isOpen: true,
        bidId,
        amount: notification.data?.amount || null,
        leagueId: notificationLeagueId
      });
      return;
    }

    // For accept / reject we call API directly
    const apiAction = ACTION_MAP[actionKey] || actionKey;

    setActionLoading(bidId);
    try {
      await NotificationService.respondToBid(bidId, apiAction, null, notificationLeagueId);

      // Mark the notification as read so that it is not returned in future
      try {
        await NotificationService.markAsRead(notificationId);
      } catch (markErr) {
        console.warn('Failed to mark notification as read:', markErr);
      }

      setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
      toast.success(apiAction === 'reject' ? 'Offer rejected' : 'Offer accepted');

      // Trigger badge count refresh so the unread counter decreases
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error responding to bid:', err);
      toast.error(err.response?.data?.error || err.message || 'Could not respond to this offer.');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle navigation actions
  const handleNavigationAction = (action, notificationId) => {
    const notif = notifications.find(n => n._id === notificationId);
    const leagueSlug = notif?.data?.leagueSlug || notif?.leagueSlug || activeLeague?.slug;
    const path = resolveNotificationNavigationPath(action, {
      notification: notif,
      leagueSlug,
      teamId: currentTeamId
    });

    if (!path) {
      return;
    }

    // Mark notification as read
    dismissNotification(notificationId);

    // Navigate to the path
    navigate(path);
  };

  // Handle counter popup success
  const handleCounterSuccess = async () => {
    setCounterPopup({ isOpen: false, bidId: null, amount: null, leagueId: null });

    // Flag related notification as read/handled
    if (counterPopup.bidId) {
      const notificationId = notifications.find(n => n.data?.bidId === counterPopup.bidId)?._id;
      if (notificationId) {
        // Update local state
        setNotifications(prev => prev.map(notif => (
          notif._id === notificationId ? { ...notif, read: true } : notif
        )));

        // Persist read status
        try {
          await NotificationService.markAsRead(notificationId);
        } catch (err) {
          console.warn('Failed to mark notification as read:', err);
        }
      }
    }
    if (onRefresh) onRefresh();
  };

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If a popup (class 'popup') is present and the click occurs inside it, do **not** treat it
      // as an outside-click for the notifications dropdown. This prevents the dropdown’s
      // handler from immediately closing when interacting with the CounterBidPopup (or
      // another popup), which lives outside `dropdownRef`.
      if (event.target.closest('.popup')) {
        return; // ignore – user is interacting with a popup
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        console.log('Click outside notification dropdown detected');
        onClose();
      }
    };

    if (isOpen) {
      console.log('Adding click outside event listener');
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      console.log('Removing click outside event listener');
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Load notifications when dropdown is opened
  useEffect(() => {
    console.log('NotificationsDropdown isOpen or filter changed. isOpen:', isOpen, 'filter:', filter);
    if (isOpen) {
      loadNotifications();
    }
  }, [filter, hasError, isOpen, loadNotifications, refreshSignal]); // Reload if any of these change while open

  if (!isOpen) return null;

  console.log('Rendering NotificationsDropdown, notifications:', notifications);

  // Helper to get notification icon
  const getNotificationIcon = (item) => {
    if (item.type === 'transfer') {
      switch (item.data?.status) {
        case 'auction_won':
          return '🎯'; // Won
        case 'auction_lost':
          return '❌'; // Lost
        case 'player_transferred_elsewhere':
          return '🚫';
        case 'outbid':
          return '📈'; // Outbid
        case 'bid_received':
          return '📥'; // Bid received
        case 'accepted': // Manual bid acceptance
          return '✅';
        case 'rejected': // Manual bid rejection
          return '🚫';
        case 'completed': // Transfer completed (e.g. after auction or direct acceptance)
          return '🤝';
        case 'budget':
          return '💰';
        case 'free_agent_bid':
          return '📈';
        case 'counter_received':
          return '🔄';
        default:
          return '💸'; // Default transfer icon
      }
    } else if (item.type === 'match') {
      // Note: 'match_ready' notifications use type 'match' and data.action 'league'
      if (item.data?.action === 'league') { 
        return '⚽'; // Match ready/upcoming
      } else if (item.data?.action === 'result') {
        return '📊'; // Match result
      }
      return '🏅'; // Default match icon
    } else if (item.type === 'league') {
      return '🏆'; // Default league icon
    } else if (item.type === 'system') {
      return '⚙️'; // Default system icon
    }
    return null; // No icon for unknown types or if data is missing
  };

  // Render notifications
  const renderNotifications = () => {
    if (loading) {
      return null; // No loading text to avoid jarring flash
    }
    
    if (error || hasError) {
      return (
        <div className="notification-error">
          <div>{error || 'Failed to load notifications'}</div>
          <button 
            className="retry-button" 
            onClick={() => {
              if (onRetry) onRetry();
              loadNotifications();
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    
    if (notifications.length === 0) {
      return <div className="notification-empty">No new notifications</div>;
    }
    
    return notifications.map((item, index) => {
      // Determine notification text
      const serverText = item.text;
      let notificationText = '';
      
      console.log('Processing notification item:', item);
      
      // Use text from virtual property if it exists
      if (serverText) {
        notificationText = serverText;
      } 
      // Otherwise form text based on type and data
      else if (item.type === 'transfer') {
        if (item.data?.status === 'bid_received') {
          notificationText = <>You have received a new bid for <strong>{item.data.playerName}</strong> of <strong>€{item.data.amount}</strong> from <strong>{item.data.team}</strong>.</>;
        } else if (item.data?.status === 'outbid') {
          notificationText = <>You have been outbid for <strong>{item.data.playerName}</strong> by <strong>{item.data.team}</strong>.</>;
        } else if (item.data?.status === 'free_agent_bid') {
          notificationText = <>A new highest bid of <strong>€{item.data.amount}</strong> for <strong>{item.data.playerName}</strong> was placed by <strong>{item.data.team}</strong>.</>;
        } else if (item.data?.status === 'counter_received') {
          notificationText = <>You received a counter offer of <strong>€{item.data.amount}</strong> for <strong>{item.data.playerName}</strong> from <strong>{item.data.team}</strong>.</>;
        } else if (item.data?.status === 'auction_won') {
          notificationText = <>Congratulations! You won the auction for <strong>{item.data.playerName}</strong> with a bid of <strong>€{item.data.winningBid}</strong>.</>;
        } else if (item.data?.status === 'auction_lost') {
          notificationText = <>Unfortunately, you lost the auction for <strong>{item.data.playerName}</strong>. The player was sold for <strong>€{item.data.winningBid}</strong>.</>;
        } else if (item.data?.status === 'player_transferred_elsewhere') {
          notificationText = <>Your bid for <strong>{item.data.playerName}</strong> was cancelled because the player transferred to <strong>{item.data.team || 'another team'}</strong>.</>;
        } else if (item.data?.status === 'accepted') {
          notificationText = <>Your offer for <strong>{item.data.playerName}</strong> has been accepted. The player has been transferred to your squad.</>;
        } else if (item.data?.status === 'rejected') {
          notificationText = <>Your offer for <strong>{item.data.playerName}</strong> has been rejected. Someone made a bigger offer.</>;
        } else if (item.data?.status === 'contract') {
          notificationText = <>Your offer for <strong>{item.data.playerName}</strong> has been accepted. You can now offer him a contract.</>;
        } else if (item.data?.status === 'completed') {
          notificationText = <>The transfer of <strong>{item.data.playerName}</strong> to your club is complete.</>;
        } else if (item.data?.status === 'budget') {
          notificationText = <>Your transfer budget has been boosted by <strong>€{item.data.amount}</strong>.</>;
        } else if (item.data?.status === 'prize_money') {
          notificationText = <>Your prize money for this season has been allocated to your transfer budget.</>;
        }
      } else if (item.type === 'match') {
        if (item.data?.action === 'result') {
          notificationText = <>Match result: <strong>{item.data?.homeTeam}</strong> {item.data?.homeScore} - {item.data?.awayScore} <strong>{item.data?.awayTeam}</strong></>;
        } else if (item.data?.action === 'league') { // For match_ready type notifications
          if (item.data?.status === 'starting') {
            notificationText = <>Your match against <strong>{item.data.opponentTeam}</strong> is starting.</>;
          } else {
            notificationText = item.data?.lineupLockEnabled === true
              ? <>Your match vs <strong>{item.data.opponentTeam}</strong> is ready. Lineup changes are locked.</>
              : <>Your match vs <strong>{item.data.opponentTeam}</strong> is ready. Set your lineup before kickoff.</>;
          }
        } else if (item.data?.action === 'upcoming') {
          notificationText = item.data?.lineupLockEnabled === true
            ? <>Your match against <strong>{item.data.opponentTeam}</strong> starts in ~15 minutes. Lineup changes are now locked.</>
            : <>Your match against <strong>{item.data.opponentTeam}</strong> starts in ~15 minutes. Set your lineup before kickoff.</>;
        } else if (item.data?.action === 'auto_started') {
          notificationText = <>Your match against <strong>{item.data.opponentTeam}</strong> has started.</>;
        } else if (item.data?.action === 'forfeit_lose') {
          notificationText = <>You lost the match against <strong>{item.data.opponentTeam}</strong> 0:3 — no lineup was set.</>;
        } else if (item.data?.action === 'forfeit_win') {
          notificationText = <>You won 3:0 against <strong>{item.data.opponentTeam}</strong> by default — they didn't set a lineup.</>;
        } else if (item.data?.action === 'double_forfeit_no_teamsheet') {
          notificationText = <>Your match against <strong>{item.data.opponentTeam}</strong> ended in a double forfeit — neither team set a valid lineup. Both teams received a loss and 0 points.</>;
        } else if (item.data?.action === 'cancelled_no_teamsheet') {
          notificationText = <>Your match against <strong>{item.data.opponentTeam}</strong> was cancelled — neither team set a lineup.</>;
        }
      } else if (item.type === 'league') {
        console.log('League notification data:', item.data);
        if (item.data?.action === 'created') {
          notificationText = <>You have successfully created the league "<strong>{item.data.leagueName}</strong>".</>;
        } else if (item.data?.action === 'joined') {
          notificationText = <>You have joined the league "<strong>{item.data.leagueName}</strong>".</>;
        } else if (item.data?.action === 'season') {
          notificationText = <>New season has started in "<strong>{item.data.leagueName}</strong>".</>;
        } else if (item.data?.action === 'fixtures') {
          notificationText = <>Fixtures have been generated for <strong>{item.data.leagueName}</strong>.</>;
        }
      } else if (item.type === 'system') {
        notificationText = item.data?.message || 'System notification';
      }
      
      console.log('Notification text:', notificationText);
      
      const timeDif = timeDelta(Date.now(), item.createdAt);
      
      const icon = getNotificationIcon(item);
      const bidActionLeagueId = item.additionalAction?.type === 'multi'
        ? resolveLeagueIdForNotification(item)
        : null;
      const bidActionDisabledReason = !bidActionLeagueId
        ? 'This bid notification is missing league information. Refresh notifications and try again.'
        : undefined;

      return (
        <div key={item._id || index} className="notification active">
          <button 
            type="button" 
            className="n-close-btn icon-s-cross"
            onClick={() => dismissNotification(item._id)}
          />
          <div className="notification-icon-container">
            {icon && <span className="notification-icon">{icon}</span>}
          </div>
          <div className="notification-content">
            <div className="text">
              {notificationText}
            </div>
            {/* Time indicator – show "now" for <1 minute otherwise relative time */}
            <div className="time">
              {(!timeDif.days && !timeDif.hours && !timeDif.minutes)
                ? 'now'
                : `${timeDif.days ? `${timeDif.days} d ` : ''}${timeDif.hours ? `${timeDif.hours} h ` : ''}${timeDif.minutes ? `${timeDif.minutes} min ` : ''}ago`}
            </div>
            
            {/* Render action buttons */}
            {item.additionalAction && (
              <div className="notification-actions">
                {item.additionalAction.type === 'button' && (
                  <button
                    type="button"
                    className="notification-action-btn"
                    onClick={() => handleNavigationAction(item.additionalAction.action, item._id)}
                  >
                    {item.additionalAction.text}
                  </button>
                )}
                
                {item.additionalAction.type === 'multi' && (
                  <div className="notification-button-group">
                    {item.additionalAction.actions.map((action, actionIndex) => (
                      <button
                        key={actionIndex}
                        type="button"
                        className={`notification-action-btn ${action.action === 'accept_bid' ? 'accept' : action.action === 'reject_bid' ? 'reject' : 'counter'}`}
                        onClick={() => handleBidAction(item.additionalAction.bidId, action.action, item._id)}
                        disabled={actionLoading === item.additionalAction.bidId || item.read || !bidActionLeagueId}
                        title={bidActionDisabledReason}
                      >
                        {actionLoading === item.additionalAction.bidId ? '...' : action.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    console.log('Marking all notifications as read');
    try {
      await NotificationService.markAllAsRead(activeLeagueSlug);
      console.log('All notifications marked as read successfully');
      
      // Update local state according to current filter
      if (filter === 'all') {
        // Just mark all as read in local list
        setNotifications(notifications.map(notif => ({ ...notif, read: true })));
      } else {
        // If we are viewing the unread tab, clearing the list gives immediate feedback
        setNotifications([]);
      }
      
      // Trigger badge count refresh
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      
      // Still trigger badge count refresh even on error for consistency
      if (onRefresh) {
        onRefresh();
      }
    }
  };

  // Delete all notifications permanently for the user
  const clearAll = async () => {
    console.log('Clearing all notifications');
    try {
      await NotificationService.clearAll(activeLeagueSlug);
      // Remove all notifications from local state
      setNotifications([]);
      // Refresh badge counts in parent
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      console.error('Error clearing notifications:', err);
      if (onRefresh) {
        onRefresh();
      }
    }
  };

  return (
    <>
      <div ref={dropdownRef} className="notifications-block">
        <div className="notifications-header">
          <div className="notification-counts">
            <span className="total-count">{totalCount} Total</span>
            <span className="unread-count">{unreadCount} Unread</span>
            <button 
              className={`refresh-button ${isRefreshing ? 'loading' : ''}`}
              onClick={() => !isRefreshing && onRefresh()}
              title={isRefreshing ? 'Refreshing notifications' : 'Refresh notifications'}
              disabled={isRefreshing}
            >
              ⟳
            </button>
            <button 
              className="mark-all-read-button"
              onClick={markAllAsRead}
              title="Mark all as read"
              disabled={unreadCount === 0}
            >
              ✓ All
            </button>
            <button
              className="clear-all-button"
              onClick={clearAll}
              title="Clear all notifications"
              disabled={totalCount === 0}
            >
              🗑️ All
            </button>
          </div>
          <div className="notification-filters">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => onFilterChange('all')}
            >
              All
            </button>
            <button 
              className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => onFilterChange('unread')}
            >
              Unread
            </button>
          </div>
          {/* Optional type filters – disabled for now */}
          {/* <div className="notification-type-filters">
            <button
              className={`type-filter-btn ${notificationTypeFilter === 'all' ? 'active' : ''}`}
              onClick={() => onNotificationTypeChange('all')}
            >
              All Types
            </button>
            <button
              className={`type-filter-btn ${notificationTypeFilter === 'transfer' ? 'active' : ''}`}
              onClick={() => onNotificationTypeChange('transfer')}
            >
              Transfers
            </button>
            <button
              className={`type-filter-btn ${notificationTypeFilter === 'match' ? 'active' : ''}`}
              onClick={() => onNotificationTypeChange('match')}
            >
              Matches
            </button>
          </div> */}
        </div>
        <div className="s-notifications-container active">
          {renderNotifications()}
        </div>
	        <div className="notifications-footer">
	          <Link
	            to={activeLeagueSlug ? `/${activeLeagueSlug}/notifications` : '/switch-league'}
	            className="view-all-notifications-link"
	            onClick={onClose}
	          >
	            View All Notifications
	          </Link>
	        </div>
      </div>

    {/* Counter Bid Popup */}
    <CounterBidPopup
      bidId={counterPopup.bidId}
      initialAmount={counterPopup.amount}
      leagueId={counterPopup.leagueId}
      isOpen={counterPopup.isOpen}
      onClose={() => setCounterPopup({ isOpen: false, bidId: null, amount: null, leagueId: null })}
      onSuccess={handleCounterSuccess}
    />
  </>
);
};

export default NotificationsDropdown;
